import React, { useEffect, useState, JSX } from 'react';
import { Button, Col, Row, Collapse } from 'reactstrap';
import { ApiBookingSearch, ApiBookingState } from '@ibe/api';
import dayjs, { Dayjs } from 'dayjs';
import { useMeasure } from 'react-use';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faFilter } from '@fortawesome/free-solid-svg-icons';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/booking-search.en.t.json';
import Keys from '../../../../_Translations/generated/booking-search.en.t.json.keys';
import DatePicker from '../../Input/DatePicker/DatePicker';
import useConfig from '../../../Hooks/useConfig';
import CustomInput from '../../CustomInput/CustomInput';

export interface BookingSearchProps {
  onSearch: (searchParams: ApiBookingSearch) => void;
  searchData?: ApiBookingSearch;
}

const BookingSearch = (props: BookingSearchProps): JSX.Element => {
  const [initialValues] = useState<ApiBookingSearch>({
    bookingNumber: '',
    firstName: '',
    lastName: '',
    participantEmail: '',
    travelDateStart: '',
    travelDateEnd: '',
    bookingDateStart: '',
    bookingDateEnd: '',
    bookingStates: [],
    agent: {
      lastName: ''
    }
  });

  const { searchData, onSearch } = props;

  const { t } = useTranslation('booking-search', fallback, Keys);
  const config = useConfig();
  const [searchParams, setSearchParams] = useState<ApiBookingSearch>({
    ...initialValues,
    ...searchData
  });

  useEffect(() => {
    setSearchParams({
      ...initialValues,
      ...searchData
    });
  }, [initialValues, searchData]);

  const [errors, setErrors] = useState<string[]>([]);

  const [showFilters, setShowFilters] = useState<boolean>(true);
  const toggleFilters = (): void => {
    setShowFilters(!showFilters);
  };
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const isSmall = width <= 1000;

  const changeHandler = (value: string, propertyname: string): void => {
    const result = { ...searchParams };

    if (propertyname === 'agent') {
      result.agent = { lastName: value };
      setSearchParams(result);
      return;
    }
    (result as { [key: string]: string })[propertyname] = value;
    setSearchParams(result);
  };

  const checkBoxHandler = (isChecked: boolean, state: ApiBookingState): void => {
    const result = { ...searchParams };
    if (!result.bookingStates) {
      result.bookingStates = [];
    }
    if (isChecked) {
      result.bookingStates.push(state);
    } else {
      const index = result.bookingStates.findIndex((stateitem: ApiBookingState) => {
        return stateitem === state;
      });
      if (index !== -1) {
        result.bookingStates.splice(index, 1);
      }
    }
    setSearchParams(result);
  };

  const checked = (controllItem: ApiBookingState): boolean | undefined => {
    return (
      searchParams &&
      searchParams.bookingStates &&
      searchParams.bookingStates.indexOf(controllItem) > -1
    );
  };

  const toDate = (date: string | undefined): Date | undefined => {
    if (date) {
      const dateParsed = date.split('-');
      return new Date(Number(dateParsed[0]), Number(dateParsed[1]) - 1, Number(dateParsed[2]));
    }
    return undefined;
  };

  const validateAndSearch = (currentSearchParams: ApiBookingSearch): string[] => {
    const myErrors: string[] | null = validate(currentSearchParams);
    if (myErrors.length === 0) {
      onSearch(currentSearchParams);
    }
    return myErrors;
  };

  const validate = (currentSearchParams: ApiBookingSearch): string[] => {
    const bookingDateStartAsDate: Dayjs | undefined = dayjs(currentSearchParams.bookingDateStart);
    const bookingDateEndAsDate: Dayjs | undefined = dayjs(currentSearchParams.bookingDateEnd);
    const travelDateStartAsDate: Dayjs | undefined = dayjs(currentSearchParams.travelDateStart);
    const travelDateEndAsDate: Dayjs | undefined = dayjs(currentSearchParams.travelDateEnd);

    const errorArray: string[] = [];

    if (
      bookingDateStartAsDate &&
      bookingDateEndAsDate &&
      bookingDateStartAsDate.isAfter(bookingDateEndAsDate)
    ) {
      errorArray.push(t(Keys.bookingDateOrderError));
    }

    if (
      travelDateStartAsDate &&
      travelDateEndAsDate &&
      travelDateStartAsDate.isAfter(travelDateEndAsDate)
    ) {
      errorArray.push(t(Keys.travelDateOrderError));
    }
    return errorArray;
  };

  function getClassNames(): string {
    let className = 'my-2 ';

    if (isSmall) {
      className += 'justify-content-center ';
    } else {
      className += 'justify-content-start ';
    }
    return className;
  }

  function clearAllSearchFieldsAndSearch(): void {
    setErrors(validateAndSearch({ ...initialValues }));
  }

  const SearchButton = (): JSX.Element => {
    return (
      <div className={isSmall ? 'mt-2' : ''}>
        <Button
          type="button"
          className={isSmall ? '' : 'float-right'}
          onClick={(): void => setErrors(validateAndSearch(searchParams))}
          block={isSmall}
        >
          {t(Keys.search)}
        </Button>
      </div>
    );
  };

  const ClearButton = (): JSX.Element => {
    return (
      <div className={isSmall ? 'mt-2' : ''}>
        <Button
          type="button"
          className={isSmall ? '' : 'float-right'}
          onClick={clearAllSearchFieldsAndSearch}
          block={isSmall}
        >
          {t(Keys.clear)}
        </Button>
      </div>
    );
  };

  const MobileFilters = (): JSX.Element => {
    return (
      <div className="w-100">
        <Row className="my-1 mb-2 align-items-center">
          <Col className="">
            <Row className="px-2">
              <div className="mr-1">
                <ClearButton />
              </div>
              <div className="ml-1">
                <SearchButton />
              </div>
            </Row>
          </Col>
        </Row>
        <div className="my-1" onClick={(): void => toggleFilters()}>
          <FontAwesomeIcon icon={faFilter} className="text-primary" />
          <span className="mx-2 text-primary">{t(Keys.filter)}</span>
          {!showFilters && (
            <FontAwesomeIcon icon={faAngleDown} className="fas fa-sm text-primary" />
          )}
          {showFilters && <FontAwesomeIcon icon={faAngleUp} className="fas fa-sm text-primary" />}
          <span />
        </div>

        <Collapse isOpen={showFilters}>
          <Col className="border border-dark pb-1">
            <Row className="justify-content-start ml-0 my-1">
              <CustomInput
                id="pendingStateToggle"
                onChange={(event): void =>
                  checkBoxHandler(event.target.checked, ApiBookingState.PENDING)
                }
                type="checkbox"
                label={t(Keys.pending)}
                checked={checked(ApiBookingState.PENDING)}
              />
            </Row>
            <Row className="justify-content-start ml-0 my-1">
              <CustomInput
                id="bookedStateToggle"
                onChange={(event): void =>
                  checkBoxHandler(event.target.checked, ApiBookingState.BOOKED)
                }
                type="checkbox"
                label={t(Keys.booked)}
                checked={checked(ApiBookingState.BOOKED)}
              />
            </Row>
            <Row className="justify-content-start ml-0 my-1">
              <CustomInput
                id="cancelledStateToggle"
                onChange={(event): void =>
                  checkBoxHandler(event.target.checked, ApiBookingState.CANCELLED)
                }
                type="checkbox"
                label={t(Keys.canceled)}
                checked={checked(ApiBookingState.CANCELLED)}
              />
            </Row>
            <Row className="justify-content-start ml-0 my-1">
              <CustomInput
                id="requestStateToggle"
                onChange={(event): void =>
                  checkBoxHandler(event.target.checked, ApiBookingState.REQUEST)
                }
                type="checkbox"
                label={t(Keys.request)}
                checked={checked(ApiBookingState.REQUEST)}
              />
            </Row>
            <Row className="justify-content-start ml-0 my-1">
              <CustomInput
                id="optionStateToggle"
                onChange={(event): void =>
                  checkBoxHandler(event.target.checked, ApiBookingState.OPTION)
                }
                type="checkbox"
                label={t(Keys.option)}
                checked={checked(ApiBookingState.OPTION)}
              />
            </Row>
            <Row className="justify-content-start ml-0 my-1">
              <CustomInput
                id="quoteStateToggle"
                onChange={(event): void =>
                  checkBoxHandler(event.target.checked, ApiBookingState.QUOTE)
                }
                type="checkbox"
                label={t(Keys.quote)}
                checked={checked(ApiBookingState.QUOTE)}
              />
            </Row>
          </Col>
        </Collapse>
      </div>
    );
  };

  const Filters = (): JSX.Element => {
    return (
      <Row>
        <Col className="col-12 col-lg-8">
          <Row className={getClassNames()}>
            <Col className="col-12 col-md-2 col-lg-1 iso__booking-search-filter_option">
              <div className="">
                <CustomInput
                  id="pendingStateToggle"
                  onChange={(event): void =>
                    checkBoxHandler(event.target.checked, ApiBookingState.PENDING)
                  }
                  type="checkbox"
                  label={t(Keys.pending)}
                  checked={checked(ApiBookingState.PENDING)}
                />
              </div>
            </Col>
            <Col className="col-12 col-md-2 col-lg-1 iso__booking-search-filter_option">
              <div className="">
                <CustomInput
                  id="bookedStateToggle"
                  onChange={(event): void =>
                    checkBoxHandler(event.target.checked, ApiBookingState.BOOKED)
                  }
                  type="checkbox"
                  label={t(Keys.booked)}
                  checked={checked(ApiBookingState.BOOKED)}
                />
              </div>
            </Col>
            <Col className="col-12 col-md-2 col-lg-1 iso__booking-search-filter_option">
              <div className="">
                <CustomInput
                  id="cancelledStateToggle"
                  onChange={(event): void =>
                    checkBoxHandler(event.target.checked, ApiBookingState.CANCELLED)
                  }
                  type="checkbox"
                  label={t(Keys.canceled)}
                  checked={checked(ApiBookingState.CANCELLED)}
                />
              </div>
            </Col>
            <Col className="col-12 col-md-2 col-lg-1 iso__booking-search-filter_option">
              <div className="">
                <CustomInput
                  id="requestStateToggle"
                  onChange={(event): void =>
                    checkBoxHandler(event.target.checked, ApiBookingState.REQUEST)
                  }
                  type="checkbox"
                  label={t(Keys.request)}
                  checked={checked(ApiBookingState.REQUEST)}
                />
              </div>
            </Col>
            <Col className="col-12 col-md-2 col-lg-1 iso__booking-search-filter_option">
              <div className="">
                <CustomInput
                  id="optionStateToggle"
                  onChange={(event): void =>
                    checkBoxHandler(event.target.checked, ApiBookingState.OPTION)
                  }
                  type="checkbox"
                  label={t(Keys.option)}
                  checked={checked(ApiBookingState.OPTION)}
                />
              </div>
            </Col>
            <Col className="col-12 col-md-2 col-lg-1 iso__booking-search-filter_option">
              <div className="">
                <CustomInput
                  id="quoteStateToggle"
                  onChange={(event): void =>
                    checkBoxHandler(event.target.checked, ApiBookingState.QUOTE)
                  }
                  type="checkbox"
                  label={t(Keys.quote)}
                  checked={checked(ApiBookingState.QUOTE)}
                />
              </div>
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-lg-4 align-self-center">
          <Row className="justify-content-end pr-3">
            <div className="mr-1">
              <ClearButton />
            </div>
            <div className="ml-1">
              <SearchButton />
            </div>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <div ref={ref}>
      <Row className="p-3">
        <h2>{t(Keys.yourbookings)}</h2>
      </Row>

      <Row className="p-0 p-md-2">
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label htmlFor="bookingNumber">{t(Keys.bookingnumber)}</label>
            </Col>
            <Col className="col-12 col-lg-6">
              <input
                id="bookingNumber"
                placeholder={t(Keys.bookingnumber)}
                onChange={(event): void => {
                  changeHandler(event.target.value, 'bookingNumber');
                }}
                className="form-control"
                value={searchParams.bookingNumber}
                aria-label={t(Keys.bookingnumber)}
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label htmlFor="email">{t(Keys.email)}</label>
            </Col>
            <Col className="col-12 col-lg-6">
              <input
                id="email"
                placeholder={t(Keys.email)}
                onChange={(event): void => {
                  changeHandler(event.target.value, 'participantEmail');
                }}
                className="form-control"
                value={searchParams.participantEmail}
                aria-label={t(Keys.email)}
                autoComplete={'email'}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="p-0 p-md-2">
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label htmlFor="firstNameTraveler">{t(Keys.firstname_traveler)}</label>
            </Col>
            <Col className="col-12 col-lg-6">
              <input
                id="firstNameTraveler"
                onChange={(event): void => changeHandler(event.target.value, 'firstName')}
                placeholder={t(Keys.firstname_traveler)}
                className="form-control"
                value={searchParams.firstName}
                aria-label={t(Keys.firstname_traveler)}
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label htmlFor="surnameTraveler">{t(Keys.surname_traveler)}</label>
            </Col>
            <Col className="col-12 col-lg-6">
              <input
                id="surnameTraveler"
                onChange={(event): void => changeHandler(event.target.value, 'lastName')}
                placeholder={t(Keys.surname_traveler)}
                className="form-control"
                value={searchParams.lastName}
                aria-label={t(Keys.surname_traveler)}
                autoComplete={'family-name'}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="p-0 p-md-2">
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label className="mr-1" htmlFor="travelDateStart">
                {t(Keys.traveldate_start)}
              </label>
            </Col>
            <Col className="col-12 col-lg-6">
              <DatePicker
                id={'travelDateStart'}
                onChange={(date: Date): void =>
                  changeHandler(
                    date ? dayjs(date).format(config.formatDate) : '',
                    'travelDateStart'
                  )
                }
                date={toDate(searchParams.travelDateStart)}
                dateFormat="dd MMM yyyy"
                isClearable
                dataTestId="travelDateStartDatePicker"
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label className="mr-1" htmlFor="travelDateEnd">
                {t(Keys.traveldate_end)}
              </label>
            </Col>
            <Col className="col-12 col-lg-6">
              <DatePicker
                id={'travelDateEnd'}
                onChange={(date: Date): void =>
                  changeHandler(date ? dayjs(date).format(config.formatDate) : '', 'travelDateEnd')
                }
                date={toDate(searchParams.travelDateEnd)}
                dateFormat="dd MMM yyyy"
                isClearable
                dataTestId="travelDateEndDatePicker"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="p-0 p-md-2">
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label className="mr-1" htmlFor="bookingDateStart">
                {t(Keys.bookingdate_start)}
              </label>
            </Col>
            <Col className="col-12 col-lg-6">
              <DatePicker
                id={'bookingDateStart'}
                onChange={(date: Date): void =>
                  changeHandler(
                    date ? dayjs(date).format(config.formatDate) : '',
                    'bookingDateStart'
                  )
                }
                date={toDate(searchParams.bookingDateStart)}
                dateFormat="dd MMM yyyy"
                isClearable
                dataTestId="bookingDateStartDatePicker"
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label className="mr-1" htmlFor="bookingDateEnd">
                {t(Keys.bookingdate_end)}
              </label>
            </Col>
            <Col className="col-12 col-lg-6">
              <DatePicker
                id={'bookingDateEnd'}
                onChange={(date: Date): void =>
                  changeHandler(date ? dayjs(date).format(config.formatDate) : '', 'bookingDateEnd')
                }
                date={toDate(searchParams.bookingDateEnd)}
                dateFormat="dd MMM yyyy"
                isClearable
                dataTestId="bookingDateEndDatePicker"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="p-0 p-md-2">
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0">
            <Col className="col-12 col-lg-6">
              <label htmlFor="surnameAgent">{t(Keys.surname_agent)}</label>
            </Col>
            <Col className="col-12 col-lg-6">
              <input
                id="surnameAgent"
                onChange={(event): void => changeHandler(event.target.value, 'agent')}
                placeholder={t(Keys.surname_agent)}
                className="form-control"
                value={searchParams?.agent?.lastName || ''}
                aria-label={t(Keys.surname_agent)}
                autoComplete={'family-name'}
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-md-6">
          <Row className="pt-2 pt-md-0" />
        </Col>
      </Row>
      {isSmall ? <MobileFilters /> : <Filters />}
      {errors.length > 0 && (
        <Row className="mt-2 mb-2">
          <Col>
            <div className="alert alert-danger" role="alert">
              {errors.map(error => (
                <div>{error}</div>
              ))}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default BookingSearch;
